import React, {useEffect, useMemo, useRef, useState} from "react";
import Tilt from "react-parallax-tilt";
import {IoChevronDown} from "react-icons/io5";
import {Lightbox} from "./lightbox";
import {Trip, trips, thumbOf, stripOf} from "../data/trips";
import photoSizes from "../data/photoSizes.json";
import {TripMap} from "./tripMap";
import {Scrubber, useScrubber} from "./scrubber";
import {lite} from "../lite";

interface PhotosProps {
    className?: string;
}

export function Photos({className}: PhotosProps) {
    const [expandedTrips, setExpandedTrips] = useState<Set<string>>(new Set());
    const [lightboxTrip, setLightboxTrip] = useState<Trip | null>(null);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    useEffect(() => {
        if (className === "hide") {
            setExpandedTrips(new Set());
            setLightboxTrip(null);
            setLightboxIndex(null);
        }
    }, [className]);

    const toggleTrip = (id: string, card: HTMLElement) => {
        // Collapsing from deep inside a long trip (via the sticky header) would
        // otherwise leave the page scrolled far past the now-short card.
        if (expandedTrips.has(id) && card.getBoundingClientRect().top < 0) {
            requestAnimationFrame(() => card.scrollIntoView({block: "start"}));
        }
        setExpandedTrips(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    // from a map pin: open the trip and bring its card into view
    const openTripFromMap = (id: string) => {
        setExpandedTrips(prev => new Set(prev).add(id));
        requestAnimationFrame(() => document
            .querySelector(`#photos .trip-card[data-trip="${id}"]`)
            ?.scrollIntoView({behavior: "smooth", block: "start"}));
    };

    const openLightbox = (trip: Trip, index: number) => {
        setLightboxTrip(trip);
        setLightboxIndex(index);
    };

    const closeLightbox = () => {
        setLightboxTrip(null);
        setLightboxIndex(null);
    };

    if (trips.length === 0) {
        return (
            <div className={className} id="photos">
                <hr/>
                <p>coming soon...</p>
            </div>
        );
    }

    return (
        <div className={className} id="photos">
            {!lite && <TripMap active={className === "show"} onOpenTrip={openTripFromMap}/>}
            {trips.map((trip) => {
                const isExpanded = expandedTrips.has(trip.id);
                return (
                    <div
                        key={trip.id}
                        data-trip={trip.id}
                        className={`trip-card ${isExpanded ? "expanded" : ""}`}
                        onClick={lite ? undefined : (e) => toggleTrip(trip.id, e.currentTarget)}
                    >
                        <TripHeader trip={trip} sticky={isExpanded}/>
                        {lite && (
                            <p className="trip-lite-note">{trip.photos.length} photos, hidden in low-data mode</p>
                        )}
                        {!lite && !isExpanded && (
                            <TripPreview photos={trip.photos} title={trip.title}/>
                        )}
                        {isExpanded && (
                            <BalancedGrid
                                trip={trip}
                                onPhotoClick={(i) => openLightbox(trip, i)}
                            />
                        )}
                    </div>
                );
            })}
            {lightboxIndex !== null && lightboxTrip && (
                <Lightbox
                    count={lightboxTrip.photos.length}
                    renderSlide={(i) => (
                        <img
                            src={lightboxTrip.photos[i]}
                            alt=""
                            className="lightbox-image"
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    slideKey={(i) => lightboxTrip.photos[i]}
                    index={lightboxIndex}
                    onClose={closeLightbox}
                    onNavigate={setLightboxIndex}
                />
            )}
        </div>
    );
}

// Header of a trip card. While the card is expanded it sticks to the top of
// the scroll area; a zero-height sentinel above it tells us when it's actually
// pinned so the background/shadow can fade in only then.
function TripHeader({trip, sticky}: {trip: Trip; sticky: boolean}) {
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [stuck, setStuck] = useState(false);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sticky || !sentinel) {
            setStuck(false);
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            const rootTop = entry.rootBounds?.top ?? 0;
            setStuck(!entry.isIntersecting && entry.boundingClientRect.top < rootTop);
        }, {root: sentinel.closest(".vignette")});
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [sticky]);

    return (
        <>
            <div ref={sentinelRef} className="trip-sticky-sentinel"/>
            <div className={`trip-card-header${stuck ? " stuck" : ""}`}>
                <div className="blurb">
                    <h4>{trip.title}{trip.subtitle && <span className="trip-subtitle">{trip.subtitle}</span>}</h4>
                    <span className="trip-meta">{trip.date}</span>
                </div>
                <div className="trip-chevron">
                    <IoChevronDown/>
                </div>
            </div>
        </>
    );
}

const knownSizes: Record<string, number[] | undefined> = photoSizes;

// Returns a map of height/width ratios for a set of photos. Used to reserve
// correctly-proportioned space before lazy-loaded images arrive (so they don't
// collapse to vertical lines). Sizes come from photoSizes.json (generated by
// scripts/photo-thumbs.sh) so nothing is downloaded just to measure it; any
// photo missing from that file falls back to measuring its thumbnail.
function useImageRatios(photos: string[]) {
    const [ratios, setRatios] = useState<Map<string, number>>(() => {
        const known = new Map<string, number>();
        photos.forEach(src => {
            const size = knownSizes[src];
            if (size) known.set(src, size[1] / size[0]);
        });
        return known;
    });
    useEffect(() => {
        photos.forEach(src => {
            if (knownSizes[src]) return;
            const img = new Image();
            img.onload = () => {
                setRatios(p => new Map(p).set(src, img.naturalHeight / img.naturalWidth));
            };
            img.src = thumbOf(src);
        });
    }, [photos]);
    return ratios;
}

function TripPreview({photos, title}: {photos: string[]; title: string}) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const ratios = useImageRatios(photos);
    const scrubber = useScrubber(scrollRef, [photos, ratios]);

    return (
        <div className="trip-preview-wrap">
            <div ref={scrollRef} className="trip-preview">
                {photos.map((src) => {
                    const ratio = ratios.get(src) ?? 0.75;
                    return (
                        <img
                            key={src}
                            src={stripOf(src)}
                            alt={title}
                            loading="lazy"
                            style={{aspectRatio: String(1 / ratio)}}
                        />
                    );
                })}
            </div>
            <Scrubber scrubber={scrubber}/>
        </div>
    );
}

function useColumnCount() {
    const [cols, setCols] = useState(() => window.innerWidth <= 640 ? 2 : 3);
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 640px)");
        const handler = () => setCols(mq.matches ? 2 : 3);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);
    return cols;
}

function BalancedGrid({trip, onPhotoClick}: {trip: Trip; onPhotoClick: (i: number) => void}) {
    const colCount = useColumnCount();
    const ratios = useImageRatios(trip.photos);

    const columns = useMemo(() => {
        const cols: {items: {src: string; index: number}[]; height: number}[] =
            Array.from({length: colCount}, () => ({items: [], height: 0}));

        trip.photos.forEach((src, i) => {
            const ratio = ratios.get(src) ?? 0.75;
            const shortest = cols.reduce((a, b) => a.height <= b.height ? a : b);
            shortest.items.push({src, index: i});
            shortest.height += ratio;
        });

        return cols;
    }, [trip.photos, ratios, colCount]);

    return (
        <div className="photo-grid" onClick={(e) => e.stopPropagation()}>
            {columns.map((col, c) => (
                <div key={c} className="photo-grid-column">
                    {col.items.map(({src, index}) => (
                        <Tilt key={src} className="tilt">
                            <img
                                src={thumbOf(src)}
                                alt={`${trip.title} ${index + 1}`}
                                loading="lazy"
                                style={{aspectRatio: String(1 / (ratios.get(src) ?? 0.75))}}
                                onClick={(e) => { e.stopPropagation(); onPhotoClick(index); }}
                            />
                        </Tilt>
                    ))}
                </div>
            ))}
        </div>
    );
}

